import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  webLoadingFalse,
  webLoadingTrue,
  webNavigate,
} from "../../services/navigate.service";
import { extractValues } from "../../validation/ValidationInput";
import {
  createRoles,
  getDetailsRoles,
  getListRolesPermission,
  getRoles,
  updateRoles,
} from "../../services/roles.service";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { coreFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import { useResponsive } from "../../hook/useResponsive";
import { useFormKeyboard } from "../../hook/useFormKeyboard";
import { useAccessContext } from "../../hook/useAccessContext";
import { canEditWebRole, roleTabsFor } from "../../services/accessContext";
import {
  BORDER_SOFT,
  BORDER_STRONG,
  TEAL,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  formatUpdatedAt,
} from "../../components/list";
import { SegmentedControl, STICKY_FOOTER_HEIGHT, StickyFooter } from "../../components/mobile";
import {
  EMPTY_ROLE_FORM,
  GROUP_DATA_TAGS,
  RoleForm,
  RoleGroup,
  RoleModule,
  RoleScope,
  allModules,
  buildRolePayload,
  copyModuleValues,
  countAll,
  countOn,
  detailChanges,
  dropsOwnRolesAccess,
  groupState,
  implicitAllModules,
  isLandingValid,
  isLocked,
  isRoleScope,
  isSystemRole,
  landingOptions,
  moduleChanges,
  nextLandingId,
  parentOf,
  roleToForm,
  savedAccessGroups,
  scopeGroups,
  setGroup,
  setModule,
  unusableModules,
  validateRole,
} from "./roleLogic";
import {
  BackLink,
  Badge,
  FieldErrorText,
  FieldHint,
  H2,
  INK,
  Label,
  Notice,
  PANEL,
  PillButton,
  RoleDialog,
  RoleStyles,
  SURFACE_SOFT,
  Switch,
  TextButton,
  TriCheck,
  UnsavedBar,
  changeSummary,
  fieldStyle,
  textAreaStyle,
  useBeforeUnload,
  useHostLeaveGuard,
} from "./roleShared";
import { LeaveProceed } from "../../services/leaveGuard";

// An older API reported the rank under its misspelled column name.
const ROLE_FIELD_ALIASES: Record<string, string> = { roleHirerarchy: "roleHierarchy" };

// Group order (and module order inside a group) as the host sidebar shows them; others follow in API order.
const MENU_ORDER = [
  { title: "Home", children: ["Dashboard"] },
  {
    title: "Company",
    children: ["Company Details", "Billing", "Users", "Invoice Configurations", "Locations", "Floor Plans", "Sale Points", "Reservation Setups", "Employees", "Clients"],
  },
  { title: "Reservations", children: ["Table Reservations", "Reservation Setups"] },
  { title: "Inventory", children: ["Items", "Attributes", "Inventory"] },
  {
    title: "Inventory Management",
    children: ["Orders", "Transfers", "Supplier Return", "Deliveries", "Count", "Count Approval", "Corrections", "Order Templates", "Order Approval", "Supplier Inventory"],
  },
  { title: "Device Configurations", children: ["Devices", "Device Users", "Device Tender", "Device Health"] },
  { title: "Settings", children: ["Reasons", "Schedules", "Promotion"] },
  { title: "Administration", children: ["Tax", "Payment Terms", "Roles", "Languages"] },
  {
    title: "Finance",
    children: ["Purchases", "Sales", "Assets", "Purchase Ledger", "Sales Ledger", "Transaction Month", "Journal Entries", "Financial Reports"],
  },
  { title: "Report", children: ["General Report", "Item Movement", "Recent Sales", "Product Performance"] },
];

const lower = (value: unknown) => String(value ?? "").trim().toLowerCase();

const orderGroups = (raw: any[]): RoleGroup[] => {
  const groups: RoleGroup[] = (raw ?? []).map((g: any) => ({
    id: g.id,
    name: String(g.name ?? ""),
    value: (g.value ?? []).map((m: any) => ({ id: String(m.id), name: String(m.name ?? ""), value: m.value === true })),
  }));
  const sortChildren = (modules: RoleModule[], order: string[]) => {
    const ordered = order.map((name) => modules.find((m) => lower(m.name) === lower(name))).filter(Boolean) as RoleModule[];
    return [...ordered, ...modules.filter((m) => !ordered.includes(m))];
  };
  const known = MENU_ORDER.map((menu) => {
    const group = groups.find((g) => lower(g.name) === lower(menu.title));
    return group ? { ...group, value: sortChildren(group.value, menu.children) } : null;
  }).filter(Boolean) as RoleGroup[];
  return [...known, ...groups.filter((g) => !known.some((k) => k.id === g.id))];
};

const allOff = (groups: RoleGroup[]): RoleGroup[] => groups.map((g) => ({ ...g, value: g.value.map((m) => ({ ...m, value: false })) }));

type LoadState = "loading" | "ready" | "error";

const CreateRoles = () => {
  const path = window.location.pathname;
  const search = window.location.search;
  const id: string = path.split("/").pop() || "";
  const isNew = id === "new";
  const params: any = extractValues(search.split("&"));
  const copyFrom: string = isNew && typeof params.copyFrom === "string" ? params.copyFrom : "";
  const preLanguage = localStorage.getItem("language");
  const access = useAccessContext();
  const { isMobile, isTouchLayout } = useResponsive();
  const kb = useFormKeyboard({ autoFocus: isNew && !isMobile });
  const limits = useFieldLimits(coreFormLimits("user-role"));
  const [language, setLanguage] = useState<any>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [role, setRole] = useState<any>(null);
  const [initialForm, setInitialForm] = useState<RoleForm>(EMPTY_ROLE_FORM);
  const [form, setForm] = useState<RoleForm>(EMPTY_ROLE_FORM);
  const [initialGroups, setInitialGroups] = useState<RoleGroup[]>([]);
  const [groups, setGroups] = useState<RoleGroup[]>([]);
  const [newScope, setNewScope] = useState<RoleScope>("tenant");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string>("");
  const [touchTab, setTouchTab] = useState<"details" | "permissions">("details");
  const [moduleSearch, setModuleSearch] = useState("");
  const [grantedOnly, setGrantedOnly] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyQuery, setCopyQuery] = useState("");
  const [copyRows, setCopyRows] = useState<any[]>([]);
  const [copyState, setCopyState] = useState<"idle" | "loading" | "error">("idle");
  const [saving, setSaving] = useState(false);
  const [implicitAll, setImplicitAll] = useState(false);
  const [confirmOwnLockout, setConfirmOwnLockout] = useState(false);
  const dirtyRef = useRef(false);
  const leaveProceed = useRef<LeaveProceed>(null);

  // The host's dictionary directly too: load() runs before the language state is set.
  const tr = (key: string) => {
    const dict = language ?? window.languageContext?.language;
    return dict && dict[key] ? dict[key] : key;
  };
  const fill = (key: string, values: Record<string, string | number>) =>
    Object.keys(values).reduce((text, name) => text.split(`{${name}}`).join(String(values[name])), tr(key));

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const scope: RoleScope = isNew ? newScope : isRoleScope(role?.roleScope) ? role.roleScope : "tenant";
  const readOnly = isNew ? !access.canEditRoles : !canEditWebRole(access, role);
  const nameLocked = !isNew && isSystemRole(role);

  /* ---------------- Loading ---------------- */

  const resetTo = (nextForm: RoleForm, nextGroups: RoleGroup[], baseForm: RoleForm, baseGroups: RoleGroup[]) => {
    setInitialForm(baseForm);
    setForm(nextForm);
    setInitialGroups(baseGroups);
    setGroups(nextGroups);
    setErrors({});
  };

  const load = async (keepScreen = false) => {
    if (!keepScreen) setLoadState("loading");
    webLoadingTrue();
    try {
      if (!isNew) {
        const response: any = await getDetailsRoles(id);
        const loaded = response.data?.userRole ?? {};
        const rawGroups = orderGroups(response.data?.dataModule ?? []);
        // A role with no saved pages has every page today; show it that way so a save keeps it.
        const loadedGroups = savedAccessGroups(loaded, rawGroups);
        const loadedForm = roleToForm(loaded);
        setRole(loaded);
        setImplicitAll(implicitAllModules(loaded, countOn(rawGroups), countAll(rawGroups)));
        resetTo(loadedForm, loadedGroups, loadedForm, loadedGroups);
      } else if (copyFrom) {
        const response: any = await getDetailsRoles(copyFrom);
        const source = response.data?.userRole ?? {};
        const sourceGroups = savedAccessGroups(source, orderGroups(response.data?.dataModule ?? []));
        const sourceScope: RoleScope = isRoleScope(source.roleScope) ? source.roleScope : "tenant";
        setNewScope(sourceScope);
        setRole(null);
        setImplicitAll(false);
        resetTo(
          { ...roleToForm(source), roleName: fill("Copy of {name}", { name: String(source.roleName ?? "") }) },
          sourceGroups,
          EMPTY_ROLE_FORM,
          allOff(sourceGroups),
        );
      } else {
        const response: any = await getListRolesPermission();
        const moduleGroups = allOff(orderGroups(response.data?.data ?? []));
        const wantsSupport = params.scope === "support" && roleTabsFor(access).includes("support");
        setNewScope(wantsSupport ? "support" : "tenant");
        setRole(null);
        setImplicitAll(false);
        resetTo(EMPTY_ROLE_FORM, moduleGroups, EMPTY_ROLE_FORM, moduleGroups);
      }
      setNotice("");
      setLoadState("ready");
    } catch (error) {
      console.error("Error loading role:", error);
      setLoadState((current) => (keepScreen && current === "ready" ? current : "error"));
      if (keepScreen) showApiError(error, { language });
    } finally {
      webLoadingFalse();
    }
  };

  useEffect(() => {
    load();
    // A token refresh reloads the role, but never over unsaved edits.
    const handleNewToken = () => {
      if (!dirtyRef.current) load(true);
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, [path, search]);

  // A new support role needs the access context, which can arrive after the first render.
  useEffect(() => {
    if (isNew && !copyFrom) {
      setNewScope(params.scope === "support" && roleTabsFor(access).includes("support") ? "support" : "tenant");
    }
  }, [access]);

  /* ---------------- Changes ---------------- */

  // A support role holds only what the support gate serves; the rest is hidden and saved as off.
  const shownGroups = scopeGroups(groups, scope);
  const moduleDiff = useMemo(
    () => moduleChanges(scopeGroups(initialGroups, scope), scopeGroups(groups, scope)),
    [initialGroups, groups, scope],
  );
  const changedDetails = detailChanges(initialForm, form);
  const changeItems = [
    ...changedDetails.map(tr),
    ...moduleDiff.added.map((m) => "+" + tr(m.name)),
    ...moduleDiff.removed.map((m) => "−" + tr(m.name)),
  ];
  const dirty = !readOnly && changeItems.length > 0;
  dirtyRef.current = dirty;
  useBeforeUnload(dirty, dirtyRef);
  // A company switch or other host leave while dirty opens the leave dialog instead.
  useHostLeaveGuard(dirtyRef, (proceed) => {
    leaveProceed.current = proceed;
    setLeaveTo("");
  });

  const onCount = countOn(shownGroups);
  const totalCount = countAll(shownGroups);
  const moduleById = (moduleId: string) => allModules(groups).find((m) => m.id === moduleId);

  const applyGroups = (next: RoleGroup[]) => {
    setGroups(next);
    const landing = nextLandingId(form.loadModuleId, landingOptions(groups, scope), landingOptions(next, scope));
    if (landing !== form.loadModuleId) {
      setForm((prev) => ({ ...prev, loadModuleId: landing }));
      if (landing) setErrors((prev) => ({ ...prev, loadModuleId: "" }));
      else {
        const name = tr(moduleById(form.loadModuleId)?.name ?? "");
        setNotice(fill("Landing page cleared: {module} is now switched off. Choose another page under Details.", { module: name }));
      }
    }
    if (countOn(scopeGroups(next, scope)) > 0 && errors.modules) setErrors((prev) => ({ ...prev, modules: "" }));
  };

  const toggleModule = (moduleId: string, value: boolean) => !readOnly && applyGroups(setModule(groups, moduleId, value));
  const toggleGroup = (groupId: string | number, value: boolean) => !readOnly && applyGroups(setGroup(groups, groupId, value));

  const setField = (name: keyof RoleForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const discard = () => {
    setForm(initialForm);
    setGroups(initialGroups);
    setErrors({});
    setNotice("");
  };

  /* ---------------- Navigation ---------------- */

  const listPath = "/" + preLanguage + "/roles?tab=" + (scope === "support" ? "support" : "system");
  const guardedNavigate = (to: string) => {
    if (dirty) setLeaveTo(to);
    else webNavigate(to);
  };

  /* ---------------- Save ---------------- */

  const handleSave = async () => {
    if (readOnly || saving) return;
    const limitErrors = validateAgainstLimits({ ...form }, limits, language);
    const ruleErrors = validateRole(form, groups, scope);
    const nextErrors: Record<string, string> = { ...limitErrors };
    Object.keys(ruleErrors).forEach((key) => {
      nextErrors[key] = tr(ruleErrors[key]);
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      if (isTouchLayout) setTouchTab(nextErrors.modules && Object.keys(nextErrors).length === 1 ? "permissions" : "details");
      return;
    }
    if (!isNew && dropsOwnRolesAccess(role, scopeGroups(initialGroups, scope), shownGroups)) {
      setConfirmOwnLockout(true);
      return;
    }
    await persist();
  };

  const persist = async () => {
    if (readOnly || saving) return;
    setSaving(true);
    webLoadingTrue();
    try {
      if (isNew) {
        const response: any = await createRoles(buildRolePayload(form, groups, { isNew: true, roleScope: scope }));
        const createdId = response?.data?.data?.id ?? response?.data?.id;
        showToast({ message: fill("{name} created", { name: form.roleName.trim() }), type: "success", duration: 4000 });
        dirtyRef.current = false;
        setInitialForm(form);
        setInitialGroups(groups);
        webNavigate(createdId ? "/" + preLanguage + "/roles/" + encodeURIComponent(String(createdId)) : listPath);
      } else {
        await updateRoles(id, buildRolePayload(form, groups, { isNew: false, roleScope: scope }));
        showToast({ message: fill("{name} saved", { name: form.roleName.trim() }), type: "success", duration: 4000 });
        setInitialForm(form);
        setInitialGroups(groups);
        await load(true);
      }
    } catch (error: any) {
      showApiError(error, { language, setFieldErrors: setErrors, fieldAliases: ROLE_FIELD_ALIASES });
    } finally {
      setSaving(false);
      webLoadingFalse();
    }
  };

  /* ---------------- Copy from role ---------------- */

  useEffect(() => {
    if (!copyOpen) return undefined;
    let alive = true;
    setCopyState("loading");
    const timer = setTimeout(async () => {
      try {
        const response: any = await getRoles({
          page: 1,
          limit: 20,
          searchValue: copyQuery.trim(),
          ...(scope === "support" ? { scope: "support" as const } : {}),
        });
        if (!alive) return;
        setCopyRows((response.data?.data ?? []).filter((r: any) => String(r.id) !== id));
        setCopyState("idle");
      } catch (error) {
        if (alive) setCopyState("error");
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [copyOpen, copyQuery]);

  const copyFromRole = async (source: any) => {
    webLoadingTrue();
    try {
      const response: any = await getDetailsRoles(String(source.id));
      const sourceGroups = savedAccessGroups(response.data?.userRole, orderGroups(response.data?.dataModule ?? []));
      applyGroups(copyModuleValues(groups, sourceGroups));
      setNotice(fill("Copied the permissions of {name}. Review the changes, then save.", { name: String(source.roleName ?? "") }));
      setCopyOpen(false);
    } catch (error) {
      showApiError(error, { language });
    } finally {
      webLoadingFalse();
    }
  };

  /* ---------------- Rendering ---------------- */

  const touch = isTouchLayout;
  const scopeChip =
    scope === "support" ? tr("InnovX support") : scope === "platform" ? tr("InnovX only") : tr("Shared by all companies");
  const title = isNew
    ? form.roleName.trim() || tr(scope === "support" ? "New support role" : "New web role")
    : initialForm.roleName || tr("Role");
  const userCount = Number(role?.userCount ?? 0);
  const lastUpdated = !isNew && role?.updatedAt ? formatUpdatedAt(role.updatedAt, language) : "";

  const header = (
    <header style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: touch ? "22px" : "26px", fontWeight: 600, lineHeight: 1.25, color: INK, overflowWrap: "anywhere" }}>
          {title}
        </h1>
        {!isNew && <Badge label={userCount === 1 ? tr("1 user here") : fill("{n} users here", { n: userCount })} kind="off" />}
        <Badge label={scopeChip} kind="teal" />
        {!isNew && role?.isOwnRole === true && <Badge label={tr("Your role")} kind="ok" />}
      </div>
      {lastUpdated && (
        <p style={{ margin: 0, fontSize: "14px", color: TEXT_MUTED, fontVariantNumeric: "tabular-nums" }}>
          {fill("Last updated {date}", { date: lastUpdated })}
        </p>
      )}
    </header>
  );

  const errorFor = (name: string) => (errors[name] ? <FieldErrorText id={`role-${name}-error`}>{errors[name]}</FieldErrorText> : null);
  const describedBy = (name: string, hintId?: string) =>
    [errors[name] ? `role-${name}-error` : "", hintId ?? ""].filter(Boolean).join(" ") || undefined;
  const options = landingOptions(groups, scope);
  const readOnlyStyle: React.CSSProperties = readOnly ? { backgroundColor: SURFACE_SOFT } : {};
  const landingModule = moduleById(form.loadModuleId);
  const savedLandingOff = !!form.loadModuleId && !isLandingValid(groups, form.loadModuleId, scope);

  const detailsPanel = (
    <section aria-labelledby="role-details-title" style={{ ...PANEL, padding: touch ? "16px" : "20px" }}>
      <h2 id="role-details-title" style={H2}>
        {tr("Details")}
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: touch ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
          gap: touch ? "16px" : "20px 24px",
          marginTop: "16px",
        }}
      >
        <div>
          <Label htmlFor="role-roleName" required>
            {tr("Role name")}
          </Label>
          <input
            id="role-roleName"
            name="roleName"
            className="role-field"
            value={form.roleName}
            readOnly={readOnly || nameLocked}
            aria-invalid={!!errors.roleName || undefined}
            aria-describedby={describedBy("roleName", nameLocked && !readOnly ? "role-name-hint" : undefined)}
            {...limitProps(limits, "roleName")}
            onChange={(e) => setField("roleName", e.target.value)}
            style={{ ...fieldStyle(touch, !!errors.roleName), ...(nameLocked ? { backgroundColor: SURFACE_SOFT } : readOnlyStyle) }}
          />
          <FieldCounter value={form.roleName} max={limits.roleName?.maxLength} />
          {nameLocked && !readOnly && <FieldHint id="role-name-hint">{tr("System role names can't be changed.")}</FieldHint>}
          {errorFor("roleName")}
        </div>
        <div>
          <Label htmlFor="role-loadModuleId" required>
            {tr("Landing page after sign-in")}
          </Label>
          <select
            id="role-loadModuleId"
            className="role-field"
            value={options.some((o) => o.id === form.loadModuleId) ? form.loadModuleId : ""}
            disabled={readOnly || options.length === 0}
            aria-invalid={!!errors.loadModuleId || undefined}
            aria-describedby={describedBy("loadModuleId", "role-landing-hint")}
            onChange={(e) => setField("loadModuleId", e.target.value)}
            style={{ ...fieldStyle(touch, !!errors.loadModuleId), cursor: readOnly ? "default" : "pointer" }}
          >
            <option value="">{tr("Choose a page")}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {`${tr(o.groupName)} > ${tr(o.moduleName)}`}
              </option>
            ))}
          </select>
          <FieldHint id="role-landing-hint">
            {options.length === 0
              ? tr("Switch on a page under Permissions first.")
              : savedLandingOff && landingModule?.value
                ? fill("The saved landing page, {module}, can't be opened by InnovX support. Choose another page.", {
                    module: tr(landingModule.name),
                  })
                : savedLandingOff
                ? fill("The saved landing page, {module}, is switched off for this role. Choose another page.", {
                    module: tr(landingModule?.name ?? ""),
                  })
                : tr("Where people with this role arrive after they sign in. Only pages switched on are listed.")}
          </FieldHint>
          {errorFor("loadModuleId")}
        </div>
      </div>
      <div style={{ marginTop: touch ? "16px" : "20px" }}>
        <Label htmlFor="role-roleDescription" required>
          {tr("Description")}
        </Label>
        <textarea
          id="role-roleDescription"
          name="roleDescription"
          className="role-field"
          value={form.roleDescription}
          readOnly={readOnly}
          aria-invalid={!!errors.roleDescription || undefined}
          aria-describedby={describedBy("roleDescription")}
          {...limitProps(limits, "roleDescription")}
          onChange={(e) => setField("roleDescription", e.target.value)}
          style={{ ...textAreaStyle(touch, !!errors.roleDescription), ...readOnlyStyle }}
        />
        <FieldCounter value={form.roleDescription} max={limits.roleDescription?.maxLength} />
        {errorFor("roleDescription")}
      </div>
      <div style={{ marginTop: "16px", borderTop: `1px solid ${BORDER_SOFT}`, paddingTop: "12px" }}>
        <button
          type="button"
          className="role-disclosure"
          aria-expanded={showAdvanced || !!errors.roleHierarchy}
          aria-controls="role-advanced"
          onClick={() => setShowAdvanced((v) => !v)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            minHeight: touch ? "44px" : "32px",
            padding: "0 4px",
            border: "none",
            borderRadius: "8px",
            background: "none",
            color: TEXT,
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <span aria-hidden="true" style={{ display: "inline-block", transform: showAdvanced || errors.roleHierarchy ? "rotate(90deg)" : "none", transition: "transform 150ms ease", color: TEXT_DIM }}>
            ›
          </span>
          {tr("Advanced: rank and notes (not enforced)")}
        </button>
        {(showAdvanced || !!errors.roleHierarchy) && (
          <div
            id="role-advanced"
            style={{
              display: "grid",
              gridTemplateColumns: touch ? "1fr" : "minmax(160px, 220px) 1fr",
              gap: touch ? "16px" : "20px 24px",
              marginTop: "12px",
            }}
          >
            <div>
              <Label htmlFor="role-roleHierarchy">{tr("Rank")}</Label>
              <input
                id="role-roleHierarchy"
                name="roleHierarchy"
                className="role-field"
                inputMode="numeric"
                value={form.roleHierarchy}
                readOnly={readOnly}
                aria-invalid={!!errors.roleHierarchy || undefined}
                aria-describedby={describedBy("roleHierarchy", "role-rank-hint")}
                {...limitProps(limits, "roleHierarchy", "number")}
                onChange={(e) => setField("roleHierarchy", e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                style={{ ...fieldStyle(touch, !!errors.roleHierarchy), ...readOnlyStyle }}
              />
              <FieldHint id="role-rank-hint">{tr("1 is the highest. For your reference only; nothing checks it.")}</FieldHint>
              {errorFor("roleHierarchy")}
            </div>
            <div>
              <Label htmlFor="role-restrictionDescription">{tr("Notes")}</Label>
              <textarea
                id="role-restrictionDescription"
                name="restrictionDescription"
                className="role-field"
                value={form.restrictionDescription}
                readOnly={readOnly}
                aria-describedby={describedBy("restrictionDescription", "role-notes-hint")}
                {...limitProps(limits, "restrictionDescription")}
                onChange={(e) => setField("restrictionDescription", e.target.value)}
                style={{ ...textAreaStyle(touch, !!errors.restrictionDescription), ...readOnlyStyle }}
              />
              <FieldCounter value={form.restrictionDescription} max={limits.restrictionDescription?.maxLength} />
              <FieldHint id="role-notes-hint">{tr("For example, what this role should not do. Nothing enforces it.")}</FieldHint>
              {errorFor("restrictionDescription")}
            </div>
          </div>
        )}
      </div>
    </section>
  );

  // Rows of one group: a module that depends on another in the same group sits indented under it.
  const rowsOf = (group: RoleGroup) => {
    const ordered: { module: RoleModule; child: boolean }[] = [];
    const placed = new Set<string>();
    const inGroup = (moduleId?: string) => !!moduleId && group.value.some((m) => m.id === moduleId);
    for (const module of group.value) {
      if (placed.has(module.id) || inGroup(parentOf(groups, module)?.id)) continue;
      ordered.push({ module, child: false });
      placed.add(module.id);
      for (const child of group.value) {
        if (!placed.has(child.id) && parentOf(groups, child)?.id === module.id) {
          ordered.push({ module: child, child: true });
          placed.add(child.id);
        }
      }
    }
    group.value.filter((m) => !placed.has(m.id)).forEach((module) => ordered.push({ module, child: false }));
    const term = lower(moduleSearch);
    return ordered.filter(({ module }) => {
      if (grantedOnly && !module.value) return false;
      if (!term) return true;
      return lower(tr(module.name)).includes(term) || lower(module.name).includes(term) || lower(tr(group.name)).includes(term);
    });
  };

  const moduleRow = (module: RoleModule, child: boolean) => {
    const locked = isLocked(groups, module);
    const parent = parentOf(groups, module);
    const disabled = readOnly || locked;
    const nameId = `role-module-${module.id}`;
    const hintId = `role-module-hint-${module.id}`;
    return (
      <div
        key={module.id}
        className={disabled ? undefined : "role-row"}
        onClick={() => !disabled && toggleModule(module.id, !module.value)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          minHeight: touch ? "52px" : "44px",
          padding: child ? "8px 12px 8px 32px" : "8px 12px",
          borderTop: `1px solid ${BORDER_SOFT}`,
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <span id={nameId} style={{ display: "block", fontSize: touch ? "15px" : "14px", fontWeight: 500, color: locked ? TEXT_DIM : TEXT }}>
            {child && <span aria-hidden="true" style={{ color: TEXT_DIM, marginRight: "6px" }}>↳</span>}
            {tr(module.name)}
          </span>
          {locked && parent && (
            <span id={hintId} style={{ display: "block", fontSize: "12.5px", color: TEXT_DIM, marginTop: "2px" }}>
              {fill("Requires {module}", { module: tr(parent.name) })}
            </span>
          )}
        </div>
        <Switch
          checked={!!module.value}
          disabled={disabled}
          label={tr(module.name)}
          describedBy={locked ? hintId : undefined}
          touch={touch}
          onChange={(next) => toggleModule(module.id, next)}
        />
      </div>
    );
  };

  const visibleGroups = shownGroups.map((group) => ({ group, rows: rowsOf(group) })).filter(({ rows }) => rows.length > 0);

  const permissionsPanel = (
    <section aria-labelledby="role-permissions-title" style={{ ...PANEL, padding: touch ? "16px" : "20px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h2 id="role-permissions-title" style={{ ...H2, display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
            {tr("Permissions")}
            <span style={{ fontSize: "13px", fontWeight: 400, color: TEXT_DIM, fontVariantNumeric: "tabular-nums" }}>
              {fill("{n} of {m} pages on", { n: onCount, m: totalCount })}
            </span>
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "14px", color: TEXT_MUTED }}>{tr("The pages people with this role can open.")}</p>
        </div>
        {!readOnly && (
          <PillButton small onClick={() => setCopyOpen(true)}>
            {tr("Copy from role")}
          </PillButton>
        )}
      </div>
      {errors.modules && <FieldErrorText>{errors.modules}</FieldErrorText>}
      {notice && (
        <div style={{ marginTop: "12px" }}>
          <Notice>{notice}</Notice>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginTop: "16px" }}>
        <input
          type="search"
          className="role-field"
          aria-label={tr("Search pages")}
          placeholder={tr("Search pages")}
          value={moduleSearch}
          onChange={(e) => setModuleSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) e.preventDefault();
          }}
          style={{ ...fieldStyle(touch), flex: touch ? "1 1 100%" : "0 1 280px", height: touch ? "44px" : "40px" }}
        />
        <label style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "14px", color: TEXT, cursor: "pointer", minHeight: "40px" }}>
          <Switch checked={grantedOnly} onChange={setGrantedOnly} label={tr("Granted only")} touch={touch} />
          <span aria-hidden="true">{tr("Granted only")}</span>
        </label>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: touch ? "1fr" : "repeat(auto-fill, minmax(360px, 1fr))",
          gap: "16px",
          alignItems: "start",
          marginTop: "16px",
        }}
      >
        {visibleGroups.length === 0 && (
          <p style={{ margin: 0, fontSize: "14px", color: TEXT_DIM }}>
            {grantedOnly && !moduleSearch ? tr("No pages are switched on yet.") : tr("No pages match your search.")}
          </p>
        )}
        {visibleGroups.map(({ group, rows }) => {
          const state = groupState(group);
          const on = group.value.filter((m) => m.value).length;
          const tag = GROUP_DATA_TAGS[lower(group.name)];
          return (
            <div key={String(group.id)} role="group" aria-label={tr(group.name)} style={{ border: `1px solid ${BORDER_SOFT}`, borderRadius: "12px", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", backgroundColor: "#FAFBFB", flexWrap: "wrap" }}>
                <TriCheck
                  state={state}
                  disabled={readOnly}
                  label={fill("All pages in {group}", { group: tr(group.name) })}
                  onChange={(next) => toggleGroup(group.id, next)}
                />
                <span style={{ fontSize: "14px", fontWeight: 600, color: TEXT }}>{tr(group.name)}</span>
                {tag && <Badge label={tr(tag)} kind="warn" />}
                <span style={{ fontSize: "13px", color: TEXT_DIM, fontVariantNumeric: "tabular-nums" }}>
                  {fill("{n} of {m}", { n: on, m: group.value.length })}
                </span>
                {!readOnly && (
                  <span style={{ marginLeft: "auto", display: "inline-flex", gap: "2px" }}>
                    <TextButton onClick={() => toggleGroup(group.id, true)} disabled={state === "all"}>
                      {tr("All")}
                    </TextButton>
                    <TextButton onClick={() => toggleGroup(group.id, false)} disabled={state === "none"}>
                      {tr("None")}
                    </TextButton>
                  </span>
                )}
              </div>
              {rows.map(({ module, child }) => moduleRow(module, child))}
            </div>
          );
        })}
      </div>
    </section>
  );

  const readOnlyNotice = readOnly && (
    <Notice>
      {access.canEditRoles
        ? tr("This role can't be changed here.")
        : tr("You can view this role but not change it. Web roles are managed by InnovX.")}
    </Notice>
  );
  const unusable = isNew ? [] : unusableModules(initialGroups, scope);
  const accessNotices = (
    <>
      {!isNew && !readOnly && implicitAll && (
        <Notice tone="warn">
          {tr("This role has no saved permissions, so holders get every page. Saving stores exactly the pages switched on here.")}
        </Notice>
      )}
      {!readOnly && unusable.length > 0 && (
        <Notice tone="warn">
          {fill("Hidden because InnovX support can't use them: {modules}. Saving switches them off.", {
            modules: unusable.map((m) => tr(m.name)).join(", "),
          })}
        </Notice>
      )}
    </>
  );

  const saveLabel = isNew ? tr("Create role") : tr("Save changes");
  const summary = isNew
    ? fill("New role · {n} pages on", { n: onCount })
    : changeSummary(changeItems, language);
  const firstError = Object.values(errors).find(Boolean) ?? "";

  const leaveDialog = (
    <RoleDialog
      open={leaveTo !== null}
      touch={touch}
      language={language}
      title={tr("Leave without saving?")}
      onClose={() => setLeaveTo(null)}
      actions={[
        { label: tr("Keep editing"), onClick: () => setLeaveTo(null) },
        {
          label: tr("Discard changes"),
          variant: "danger",
          onClick: () => {
            const to = leaveTo;
            const proceed = leaveProceed.current;
            leaveProceed.current = null;
            setLeaveTo(null);
            dirtyRef.current = false;
            if (to) webNavigate(to);
            else {
              discard();
              proceed?.();
            }
          },
        },
      ]}
    >
      <p style={{ margin: 0 }}>{fill("Your {n} unsaved changes to this role will be lost.", { n: changeItems.length })}</p>
    </RoleDialog>
  );

  const ownLockoutDialog = (
    <RoleDialog
      open={confirmOwnLockout}
      touch={touch}
      language={language}
      title={tr("Remove Roles from your own role?")}
      onClose={() => setConfirmOwnLockout(false)}
      actions={[
        { label: tr("Keep editing"), onClick: () => setConfirmOwnLockout(false) },
        {
          label: tr("Save anyway"),
          variant: "danger",
          onClick: () => {
            setConfirmOwnLockout(false);
            void persist();
          },
        },
      ]}
    >
      <p style={{ margin: 0 }}>
        {tr("You hold this role. Without Roles you can no longer open this screen, and only another InnovX role editor can give it back.")}
      </p>
    </RoleDialog>
  );

  const copyDialog = (
    <RoleDialog
      open={copyOpen}
      touch={touch}
      language={language}
      title={tr("Copy permissions from a role")}
      subtitle={tr("Replaces the switches below. Details stay as they are.")}
      onClose={() => setCopyOpen(false)}
      actions={[{ label: tr("Close"), onClick: () => setCopyOpen(false) }]}
    >
      <input
        type="search"
        className="role-field"
        aria-label={tr("Search roles")}
        placeholder={tr("Search roles")}
        value={copyQuery}
        onChange={(e) => setCopyQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        style={{ ...fieldStyle(touch), height: "40px" }}
      />
      <div role="list" style={{ marginTop: "12px", display: "flex", flexDirection: "column", maxHeight: "320px", overflowY: "auto" }}>
        {copyState === "loading" && <p style={{ margin: "8px 0", color: TEXT_DIM }}>{tr("Loading roles…")}</p>}
        {copyState === "error" && <p role="alert" style={{ margin: "8px 0", color: "#BA0E13" }}>{tr("Roles could not be loaded. Try again.")}</p>}
        {copyState === "idle" && copyRows.length === 0 && <p style={{ margin: "8px 0", color: TEXT_DIM }}>{tr("No roles match your search.")}</p>}
        {copyState === "idle" &&
          copyRows.map((r) => (
            <div role="listitem" key={String(r.id)}>
              <button
                type="button"
                className="role-option"
                onClick={() => copyFromRole(r)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  border: "none",
                  borderRadius: "8px",
                  background: "none",
                  cursor: "pointer",
                }}
              >
                <span style={{ display: "block", fontSize: "14px", fontWeight: 500, color: TEXT }}>{r.roleName}</span>
                {r.roleDescription && <span style={{ display: "block", fontSize: "12.5px", color: TEXT_DIM }}>{r.roleDescription}</span>}
              </button>
            </div>
          ))}
      </div>
    </RoleDialog>
  );

  const root = (children: React.ReactNode) => (
    <div {...kb.scopeProps}>
      <div
        className="h-full font-inter scroll-container"
        style={{ height: "100%", overflowY: "auto", fontFamily: "Inter, sans-serif", color: TEXT, display: "flex", flexDirection: "column" }}
      >
        <RoleStyles />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: touch ? "16px" : "20px",
            paddingBottom: touch && !readOnly ? `${STICKY_FOOTER_HEIGHT + 16}px` : touch ? "24px" : "8px",
            minWidth: 0,
            flex: 1,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  const backLink = <BackLink label={tr("Back to roles")} onClick={() => guardedNavigate(listPath)} />;

  if (loadState !== "ready") {
    return root(
      <>
        {backLink}
        {loadState === "error" ? (
          <div style={{ ...PANEL, padding: "24px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: 500, color: TEXT }}>{tr("This role could not be loaded.")}</p>
            <PillButton small onClick={() => load()} style={{ color: TEAL }}>
              {tr("Try again")}
            </PillButton>
          </div>
        ) : (
          <div aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[44, 180, 320].map((h) => (
              <div key={h} style={{ height: `${h}px`, borderRadius: "16px", backgroundColor: SURFACE_SOFT, border: `1px solid ${BORDER_SOFT}` }} />
            ))}
          </div>
        )}
      </>,
    );
  }

  if (touch) {
    return (
      <>
        {root(
          <>
            {backLink}
            {header}
            {readOnlyNotice}
            {accessNotices}
            <div style={{ position: "sticky", top: 0, zIndex: 5, backgroundColor: "#FFFFFF", paddingBottom: "8px" }}>
              <SegmentedControl
                ariaLabel={tr("Role sections")}
                value={touchTab}
                onChange={setTouchTab}
                options={[
                  { value: "details", label: tr("Details") },
                  { value: "permissions", label: `${tr("Permissions")} (${onCount}/${totalCount})` },
                ]}
              />
              {dirty && (
                <p style={{ margin: "8px 0 0", fontSize: "13px", color: TEXT_MUTED, display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>
                    {changeItems.length === 1 ? tr("1 unsaved change") : fill("{n} unsaved changes", { n: changeItems.length })}
                  </span>
                  <TextButton onClick={discard}>{tr("Discard")}</TextButton>
                </p>
              )}
              {touchTab === "details" && errors.modules && <FieldErrorText>{errors.modules}</FieldErrorText>}
            </div>
            {touchTab === "details" ? detailsPanel : permissionsPanel}
          </>,
        )}
        {!readOnly && (
          <StickyFooter
            cancelLabel={tr("Cancel")}
            onCancel={() => guardedNavigate(listPath)}
            primaryLabel={saveLabel}
            primaryDisabled={!dirty || saving}
            onPrimary={() => {
              void handleSave();
            }}
          />
        )}
        {leaveDialog}
        {ownLockoutDialog}
        {copyDialog}
      </>
    );
  }

  return (
    <>
      {root(
        <>
          {backLink}
          {header}
          {readOnlyNotice}
          {accessNotices}
          {detailsPanel}
          {permissionsPanel}
          {!readOnly && (
            <UnsavedBar
              language={language}
              dirty={dirty}
              count={changeItems.length}
              summary={summary}
              error={firstError}
              onDiscard={discard}
              saveButton={
                <button
                  type="button"
                  className="role-primary"
                  {...kb.primaryProps(handleSave)}
                  disabled={!dirty || saving}
                  style={{
                    height: "40px",
                    padding: "0 24px",
                    borderRadius: "999px",
                    border: "none",
                    backgroundColor: TEAL,
                    color: "#FFFFFF",
                    fontSize: "14px",
                    fontWeight: 500,
                    opacity: !dirty || saving ? 0.5 : 1,
                    cursor: !dirty || saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saveLabel}
                </button>
              }
            />
          )}
        </>,
      )}
      {leaveDialog}
      {ownLockoutDialog}
      {copyDialog}
    </>
  );
};
export default CreateRoles;
